import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { mangas, categories } from '@/mocks/mangaData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';
import SchemaOrg from '@/components/base/SchemaOrg';

const ITEMS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const category = categories.find((c) => c.slug === slug);

  const filteredMangas = useMemo(() => {
    if (!slug) return [];
    return mangas.filter((m) => m.categories.includes(slug));
  }, [slug]);

  const sortedMangas = useMemo(() => {
    const list = [...filteredMangas];
    switch (sortBy) {
      case 'latest':
        return list.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      case 'popular':
        return list.sort((a, b) => b.views - a.views);
      case 'rating':
        return list.sort((a, b) => b.rating - a.rating);
      default:
        return list;
    }
  }, [filteredMangas, sortBy]);

  const totalPages = Math.ceil(sortedMangas.length / ITEMS_PER_PAGE);
  const paginatedMangas = sortedMangas.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 pt-24 pb-14 text-center">
          <h1 className="text-2xl font-heading text-foreground-900">分类不存在</h1>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.tuhaomanhua.com/' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `https://www.tuhaomanhua.com/category/${slug}` },
    ],
  };

  return (
    <>
      <SchemaOrg data={breadcrumbSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 pt-24 pb-14">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
              {category.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">{category.description}</p>
          </div>

          {/* Sort Controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <p className="text-sm text-foreground-500">
              共 <strong className="text-foreground-900">{filteredMangas.length}</strong> 部作品
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {[
                { key: 'latest' as const, label: '最新' },
                { key: 'popular' as const, label: '最热' },
                { key: 'rating' as const, label: '评分' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => { setSortBy(opt.key); setCurrentPage(1); }}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Manga Grid */}
          {paginatedMangas.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {paginatedMangas.map((manga) => (
                  <MangaCard key={manga.slug} manga={manga} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <nav className="flex items-center justify-center gap-1 mt-10" aria-label="分页导航">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                        page === currentPage
                          ? 'bg-primary-500 text-white'
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </nav>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-book-2-line text-foreground-400 text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画作品</p>
            </div>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}